import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import BareInput from "components/DesignSystem/Input/BareInput";
import { YYYY_MM_DD } from "constants/date";
import {
  CRM_CHECKBOX_OPTIONS,
  CRM_CHECKBOX_STATE,
  CRM_MULTI_SELECT_MATCH_OPTIONS,
  CRM_TEXT_MATCH_MODE,
  CRM_TEXT_MATCH_OPTIONS,
  CrmTextMatchMode,
} from "constants/crmFieldFilter";
import dayjs from "dayjs";
import { Field, FieldProps } from "formik";
import { ChangeEvent } from "react";
import {
  CRM_FILTER_KIND,
  CRM_FILTER_MATCH,
  CrmFieldFilter,
  CrmFilterField,
  CrmFilterMatch,
} from "types/Models/crmFields";

type CrmConditionControlProps = {
  field: CrmFilterField;
  condition: CrmFieldFilter;
  onChange: (condition: CrmFieldFilter) => void;
};

const SELECT_PORTAL_TARGET = document.body;

/** Renders the one control that matches this field's filter kind. */
export const CrmConditionControl = ({
  field,
  condition,
  onChange,
}: CrmConditionControlProps) => {
  const textMatchMode: CrmTextMatchMode =
    condition.equals === undefined
      ? CRM_TEXT_MATCH_MODE.CONTAINS
      : CRM_TEXT_MATCH_MODE.EQUALS;

  const textValue = condition.equals ?? condition.contains ?? "";

  const handleTextModeChange = (mode: CrmTextMatchMode) => {
    if (mode === CRM_TEXT_MATCH_MODE.EQUALS) {
      onChange({ field_key: field.field_key, equals: textValue });
      return;
    }

    onChange({ field_key: field.field_key, contains: textValue });
  };

  const handleTextValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (textMatchMode === CRM_TEXT_MATCH_MODE.EQUALS) {
      onChange({ field_key: field.field_key, equals: value });
      return;
    }

    onChange({ field_key: field.field_key, contains: value });
  };

  const handleNumberChange =
    (operand: "min_value" | "max_value") =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const parsed = value === "" ? undefined : Number(value);

      onChange({ ...condition, [operand]: parsed });
    };

  const handleDateChange =
    (operand: "date_from" | "date_to") => (date: Date) => {
      onChange({ ...condition, [operand]: dayjs(date).format(YYYY_MM_DD) });
    };

  const handleCheckboxChange = (value: string) => {
    onChange({
      field_key: field.field_key,
      is_checked: value === CRM_CHECKBOX_STATE.CHECKED,
    });
  };

  const handleValuesChange = (values: string[]) => {
    onChange({ ...condition, values });
  };

  const handleTextModeSelect = (option: unknown) => {
    if (option && typeof option === "object" && "value" in option) {
      handleTextModeChange(option.value as CrmTextMatchMode);
    }
  };

  const handleCheckboxSelect = (option: unknown) => {
    if (option && typeof option === "object" && "value" in option) {
      handleCheckboxChange(String(option.value));
    }
  };

  const handleMatchSelect = (option: unknown) => {
    if (option && typeof option === "object" && "value" in option) {
      handleMatchChange(String(option.value));
    }
  };

  const handleOptionsSelect = (options: unknown) => {
    const picked = Array.isArray(options) ? options : [];
    handleValuesChange(picked.map(({ value }) => String(value)));
  };

  const handleMatchChange = (value: string) => {
    onChange({ ...condition, match: Number(value) as CrmFilterMatch });
  };

  const optionItems = field.options.map(({ label, value }) => ({
    label,
    value,
  }));

  const selectedOptions = optionItems.filter(({ value }) =>
    (condition.values ?? []).includes(value)
  );

  if (field.filter_kind === CRM_FILTER_KIND.TEXT) {
    return (
      <div className="t-flex t-gap-2 t-w-full">
        <div className="t-w-36 t-shrink-0">
          <Combobox
            withForm={false}
            size="small"
            name={`${field.field_key}-mode`}
            menuPortalTarget={SELECT_PORTAL_TARGET}
            isClearable={false}
            options={CRM_TEXT_MATCH_OPTIONS}
            value={CRM_TEXT_MATCH_OPTIONS.find(
              ({ value }) => value === textMatchMode
            )}
            onChange={handleTextModeSelect}
          />
        </div>
        <BareInput
          name={`${field.field_key}-text`}
          value={textValue}
          onChange={handleTextValueChange}
          placeholder="Enter value"
          customSize="small"
        />
      </div>
    );
  }

  if (field.filter_kind === CRM_FILTER_KIND.NUMBER) {
    return (
      <div className="t-flex t-gap-2 t-w-full t-items-center">
        <BareInput
          name={`${field.field_key}-min`}
          type="number"
          value={condition.min_value ?? ""}
          onChange={handleNumberChange("min_value")}
          placeholder="Min"
          customSize="small"
        />
        <span className="t-text-text-30 t-text-body-sm">to</span>
        <BareInput
          name={`${field.field_key}-max`}
          type="number"
          value={condition.max_value ?? ""}
          onChange={handleNumberChange("max_value")}
          placeholder="Max"
          customSize="small"
        />
      </div>
    );
  }

  if (field.filter_kind === CRM_FILTER_KIND.DATE) {
    return (
      <div className="t-flex t-gap-2 t-w-full t-items-center">
        <Field name={`${field.field_key}-from`}>
          {({ field: formikField }: FieldProps) => (
            <DateInput
              {...formikField}
              // The condition, not formik, is the source of truth here - the form is mounted
              // with empty initial values, so a spread formik value would blank the input out
              // every time the modal is reopened on an already applied filter
              value={condition.date_from || ""}
              customSize="small"
              placeholder="From"
              portalId={`${field.field_key}-from`}
              onDateChange={handleDateChange("date_from")}
            />
          )}
        </Field>
        <span className="t-text-text-30 t-text-body-sm">to</span>
        <Field name={`${field.field_key}-to`}>
          {({ field: formikField }: FieldProps) => (
            <DateInput
              {...formikField}
              value={condition.date_to || ""}
              customSize="small"
              placeholder="To"
              portalId={`${field.field_key}-to`}
              onDateChange={handleDateChange("date_to")}
            />
          )}
        </Field>
      </div>
    );
  }

  if (field.filter_kind === CRM_FILTER_KIND.CHECKBOX) {
    const checkboxValue =
      condition.is_checked === true
        ? CRM_CHECKBOX_STATE.CHECKED
        : CRM_CHECKBOX_STATE.UNCHECKED;

    return (
      <Combobox
        withForm={false}
        size="small"
        name={`${field.field_key}-checkbox`}
        menuPortalTarget={SELECT_PORTAL_TARGET}
        isClearable={false}
        options={CRM_CHECKBOX_OPTIONS}
        value={CRM_CHECKBOX_OPTIONS.find(
          ({ value }) => value === checkboxValue
        )}
        onChange={handleCheckboxSelect}
      />
    );
  }

  const isMultiSelect = field.filter_kind === CRM_FILTER_KIND.MULTI_SELECT;
  const matchValue = String(condition.match ?? CRM_FILTER_MATCH.ANY);

  return (
    <div className="t-flex t-gap-2 t-w-full">
      {isMultiSelect && (
        <div className="t-w-36 t-shrink-0">
          <Combobox
            withForm={false}
            size="small"
            name={`${field.field_key}-match`}
            menuPortalTarget={SELECT_PORTAL_TARGET}
            isClearable={false}
            options={CRM_MULTI_SELECT_MATCH_OPTIONS}
            value={CRM_MULTI_SELECT_MATCH_OPTIONS.find(
              ({ value }) => value === matchValue
            )}
            onChange={handleMatchSelect}
          />
        </div>
      )}
      <div className="t-grow t-min-w-0">
        <Combobox
          withForm={false}
          isMulti
          size="small"
          name={`${field.field_key}-values`}
          menuPortalTarget={SELECT_PORTAL_TARGET}
          placeholder="Select options"
          options={optionItems}
          value={selectedOptions}
          onChange={handleOptionsSelect}
        />
      </div>
    </div>
  );
};
